import { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  RESUMO_FINANCEIRO_PLATAFORMA,
  SERIE_POR_DIA_PLATAFORMA,
  QUEBRA_POR_MEIO_PAGAMENTO_PLATAFORMA,
  CUSTO_CAMPANHAS_PLATAFORMA,
  ResumoFinanceiroPlataformaData,
  SeriePorDiaPlataformaData,
  QuebraPorMeioPagamentoPlataformaData,
  CustoCampanhasPlataformaData,
  FinanceiroFiltroVars,
} from '../services/financeiroService';
import type {
  ResumoFinanceiro,
  PontoSerieFinanceira,
  QuebraFinanceira,
  CustoCampanhaCupom,
  PeriodoFinanceiro,
} from '../types/financeiro';

// Espelha hooks/useFinanceiroLiga.ts, sem parâmetro — o recorte é sempre a plataforma inteira
// (nenhuma das 4 queries aceita ligaId). Mesmas regras do molde:
// 1. `fetchPolicy: 'no-cache'` nas 4 — o hub nunca lê de cache stale entre trocas de período.
// 2. `resumoIndisponivel` distingue "servidor devolveu null" (contrato nullable) de "falha de
//    rede" (`error`).
// 3. `refetch` dispara as 4 novamente — usado pela ação "Tentar novamente".
// 4. Nenhuma soma de valor monetário aqui: o hook só repassa o que veio das 4 queries —
//    `totalReceitaInPlay` já chega pronto do servidor.

export interface UseFinanceiroPlataformaResult {
  periodo: PeriodoFinanceiro;
  setPeriodo: (periodo: PeriodoFinanceiro) => void;
  resumo: ResumoFinanceiro | null;
  serie: PontoSerieFinanceira[];
  quebraPorMeioPagamento: QuebraFinanceira[];
  campanhas: CustoCampanhaCupom[];
  loading: boolean;
  error: Error | undefined;
  resumoIndisponivel: boolean;
  refetch: () => void;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function formatDateLocal(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// Primeiro e último dia do mês corrente, por aritmética de Date LOCAL (nunca UTC) — usar UTC
// deslocaria o período para quem está em UTC-3.
function periodoMesCorrente(): PeriodoFinanceiro {
  const agora = new Date();
  const inicio = new Date(agora.getFullYear(), agora.getMonth(), 1);
  const fim = new Date(agora.getFullYear(), agora.getMonth() + 1, 0);
  return { inicio: formatDateLocal(inicio), fim: formatDateLocal(fim) };
}

export function useFinanceiroPlataforma(): UseFinanceiroPlataformaResult {
  const [periodo, setPeriodo] = useState<PeriodoFinanceiro>(periodoMesCorrente());

  const resumoQuery = useQuery<ResumoFinanceiroPlataformaData, FinanceiroFiltroVars>(
    RESUMO_FINANCEIRO_PLATAFORMA,
    { variables: { filtro: periodo }, fetchPolicy: 'no-cache' },
  );

  const serieQuery = useQuery<SeriePorDiaPlataformaData, FinanceiroFiltroVars>(
    SERIE_POR_DIA_PLATAFORMA,
    { variables: { filtro: periodo }, fetchPolicy: 'no-cache' },
  );

  const quebraPorMeioPagamentoQuery = useQuery<QuebraPorMeioPagamentoPlataformaData, FinanceiroFiltroVars>(
    QUEBRA_POR_MEIO_PAGAMENTO_PLATAFORMA,
    { variables: { filtro: periodo }, fetchPolicy: 'no-cache' },
  );

  const campanhasQuery = useQuery<CustoCampanhasPlataformaData, FinanceiroFiltroVars>(
    CUSTO_CAMPANHAS_PLATAFORMA,
    { variables: { filtro: periodo }, fetchPolicy: 'no-cache' },
  );

  const resumo = resumoQuery.data?.resumoFinanceiroPlataforma ?? null;
  const serie = serieQuery.data?.seriePorDiaPlataforma ?? [];
  const quebraPorMeioPagamento = quebraPorMeioPagamentoQuery.data?.quebraPorMeioPagamentoPlataforma ?? [];
  const campanhas = campanhasQuery.data?.custoCampanhasPlataforma ?? [];
  const loading =
    resumoQuery.loading || serieQuery.loading || quebraPorMeioPagamentoQuery.loading || campanhasQuery.loading;
  const error = resumoQuery.error || serieQuery.error || quebraPorMeioPagamentoQuery.error || campanhasQuery.error;
  const resumoIndisponivel = !resumoQuery.loading && !resumoQuery.error && resumo === null;

  const refetch = () => {
    resumoQuery.refetch();
    serieQuery.refetch();
    quebraPorMeioPagamentoQuery.refetch();
    campanhasQuery.refetch();
  };

  return {
    periodo,
    setPeriodo,
    resumo,
    serie,
    quebraPorMeioPagamento,
    campanhas,
    loading,
    error,
    resumoIndisponivel,
    refetch,
  };
}
